// Resolves an imported location code or name to one of the org's locations, operating on the
// locations injected via setLocations().

export interface LocationOption {
  id: string;
  code: string;
  name: string;
  /** "Name - addressLine1, city, region" for display (matching the my-products list);
   * falls back to `name` when absent. */
  displayName?: string;
}

let locations: LocationOption[] = [];
const resolveCache = new Map<string, string>();
const locationListeners = new Set<() => void>();

export function setLocations(next: LocationOption[]): void {
  locations = next;
  resolveCache.clear(); // results depend on the location set
  locationListeners.forEach((listener) => listener());
}

export function getLocations(): LocationOption[] {
  return locations;
}

export function subscribeLocations(listener: () => void): () => void {
  locationListeners.add(listener);
  return () => locationListeners.delete(listener);
}

/** Full display name for a resolved location code (falls back to name, then the code), or '' if none. */
export function locationLabel(code: string): string {
  if (!code) return '';
  const loc = locations.find((l) => l.code === code);
  return loc ? (loc.displayName ?? loc.name) : code;
}

/** Resolve a raw cell value (code or name) to a location code, or '' if no match. Memoized. */
export function resolveLocationCode(raw: string): string {
  const r = (raw ?? '').trim().toLowerCase();
  if (!r) return '';
  const cached = resolveCache.get(r);
  if (cached !== undefined) return cached;

  const byExactCode = locations.find((l) => l.code.toLowerCase() === r);
  const byExactName = byExactCode
    ? undefined
    : locations.find((l) => l.name.toLowerCase() === r);
  let byPartialName: LocationOption | undefined;
  if (!byExactCode && !byExactName) {
    const partial = locations.filter((l) => l.name.toLowerCase().includes(r));
    // Only resolve a substring match when it's unambiguous; multiple matches stay unresolved so the
    // user disambiguates rather than silently importing against an arbitrary location.
    byPartialName = partial.length === 1 ? partial[0] : undefined;
  }
  const code = (byExactCode ?? byExactName ?? byPartialName)?.code ?? '';

  resolveCache.set(r, code);
  return code;
}
